import { useState, useMemo } from "react";
import { TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete } from "@mui/material";
import type { ClientAssetDataSpan, ClientAssetDataStructure } from "../types";
import SpanActiveObjectDetailedInfo from "./SpanActiveObjectDetailedInfo";
import StructureActiveObjectDetailedInfo from "./StructureActiveObjectDetailedInfo";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
    selectCurrentPosition,
    selectClientAssetData,
    setActiveStructureByName,
} from "./store/reducers/deviceLinkingAppStoreSlice";
import { getDistance } from "geolib";

type ActiveObjectDetailedInfoProps = {
    className?: string;
    activeObject?: ClientAssetDataSpan | ClientAssetDataStructure;
};

export default function ActiveObjectDetailedInfo(props: ActiveObjectDetailedInfoProps): JSX.Element {
    const dispatch = useAppDispatch();

    const currentPosition = useAppSelector(selectCurrentPosition);
    const clientAssetData = useAppSelector(selectClientAssetData);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedStructure, setSelectedStructure] = useState<string | null>(null);

    const structures = clientAssetData?.data?.structures || [];

    // Sort structures by proximity to current location
    const sortedStructures = useMemo(() => {
        if (!currentPosition?.latitude || !currentPosition?.longitude) {
            return structures;
        }

        const currentLat = currentPosition.latitude;
        const currentLng = currentPosition.longitude;

        return [...structures].sort((a, b) => {
            const distanceA = getDistance({ lat: currentLat, lng: currentLng }, { lat: a.latitude, lng: a.longitude });
            const distanceB = getDistance({ lat: currentLat, lng: currentLng }, { lat: b.latitude, lng: b.longitude });
            return distanceA - distanceB;
        });
    }, [structures, currentPosition]);

    const handleOpenDialog = () => {
        setDialogOpen(true);
        setSelectedStructure(null);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedStructure(null);
    };

    const handleSelectStructure = () => {
        if (selectedStructure) {
            dispatch(setActiveStructureByName(selectedStructure));
        }
        handleCloseDialog();
    };

    // Display span info
    if (props.activeObject && "span_name" in props.activeObject)
        return (
            <SpanActiveObjectDetailedInfo
                className={props.className}
                activeSpan={props.activeObject as ClientAssetDataSpan}
            />
        );

    // Display structure info
    else if (props.activeObject && "structure_name" in props.activeObject) {
        return (
            <StructureActiveObjectDetailedInfo
                className={props.className}
                activeStructure={props.activeObject as ClientAssetDataStructure}
            />
        );

    // Prompt to select span or structure
    // Button to open dialog to select a structure
    } else {
        return (
            <div className={props.className}>
                <p>
                    Select a <b>Span</b> or <b>Structure</b> for information and devices
                </p>
                <Button variant="contained" onClick={handleOpenDialog} disabled={structures.length === 0}>
                    Select Structure
                </Button>
                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>Snap Position to a Structure</DialogTitle>
                    <DialogContent>
                        <Autocomplete
                            options={sortedStructures.map((s) => s.structure_name)}
                            value={selectedStructure}
                            onChange={(_, newValue) => setSelectedStructure(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Structure"
                                    placeholder="Search for a structure..."
                                    autoFocus
                                />
                            )}
                            sx={{ mt: 2 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        <Button onClick={handleSelectStructure} variant="contained" disabled={!selectedStructure}>
                            Select
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}
